import { UserMenu } from '@/components/ui/UserMenu';
import { UserMenuErrorFallback } from '@/components/ui/UserMenuErrorFallback';
import logoImage from '@/assets/Logo.png';
import { useSetAtom, useAtomValue } from 'jotai';
import { userInfoAtom } from '@/features/auth/state/user';
import { signOut } from 'aws-amplify/auth';
import { useNavigate, useLocation } from 'react-router';
import { PropertySearchErrorFallback } from '@/features/property-search/components/PropertySearchErrorFallback';
import { PropertySearchSkeleton } from '@/features/property-search/components';
import { GenericErrorBoundary } from '@/components/common/GenericErrorBoundary';
import { selectedPropertiesAtom } from '@/features/property-search';
import { Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Suspense, lazy } from 'react';

// Lazy load PropertySearch component
const PropertySearch = lazy(() =>
  import('@/features/property-search/components/PropertySearch').then(
    (module) => ({
      default: module.PropertySearch,
    }),
  ),
);

export const Header = () => {
  const setSelectedProperties = useSetAtom(selectedPropertiesAtom);
  const userInfo = useAtomValue(userInfoAtom);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on the home page
  const isHomePage = location.pathname === '/';

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleHomeClick = () => {
    // Clear property search selection
    setSelectedProperties([]);
    // Navigate to home
    navigate('/');
  };

  return (
    <header className="absolute top-0 z-50 w-full bg-transparent">
      <div className="relative w-full h-14 flex items-center px-2 py-4">
        {/* Left: Context related actions */}
        <div className="absolute left-2 flex items-center space-x-3">
          <Button
            onClick={handleHomeClick}
            variant="ghost"
            size="sm"
            className="p-2 hover:bg-gray-100 text-gray-800"
            title="Home"
          >
            <Home className="h-4 w-4" />
          </Button>
          <div className="w-64">
            <GenericErrorBoundary
              fallback={<PropertySearchErrorFallback compact />}
            >
              <Suspense fallback={<PropertySearchSkeleton />}>
                <PropertySearch
                  isHomePage={isHomePage}
                  onNavigateToProperty={(propertyId) => {
                    navigate(`/property/${propertyId}`);
                  }}
                />
              </Suspense>
            </GenericErrorBoundary>
          </div>
        </div>

        {/* Center: Logo */}
        <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center">
          <img src={logoImage} alt="Sauron" className="max-h-4 w-auto" />
        </div>

        {/* Right: User actions */}
        <div className="absolute right-2 flex items-center space-x-4">
          <GenericErrorBoundary fallback={<UserMenuErrorFallback />}>
            <UserMenu
              email={userInfo?.email || 'user@example.com'}
              onLogout={handleLogout}
            />
          </GenericErrorBoundary>
        </div>
      </div>
    </header>
  );
};
